/**
 * Roster Color Utilities
 *
 * Helper functions for working with the 5-slot roster identity color system.
 * Colors bind to the slot number (1-5); roster comparison numbers the selected
 * rosters 1..N in the order they were added, renumbering when one is removed.
 *
 * The slot colors themselves are defined once, as the --roster-N CSS variables
 * in src/components/roster-badge/roster-badge.scss.
 */

import {safeConsoleError} from './sanitize';

export type RosterSlot = 1 | 2 | 3 | 4 | 5;

/**
 * Get the CSS class name for a roster slot
 *
 * @param slot - Roster slot number (1-5)
 * @returns CSS class name for slot color binding
 *
 * @example
 * ```ts
 * getRosterSlotClass(1) // 'roster-badge--1'
 * getRosterSlotClass(3) // 'roster-badge--3'
 * ```
 */
export function getRosterSlotClass(slot: RosterSlot): string {
    if (!isValidRosterSlot(slot)) {
        safeConsoleError(`getRosterSlotClass: Invalid slot ${slot}. Must be 1-5.`);
        return 'roster-badge--1';
    }
    return `roster-badge--${slot}`;
}

/**
 * Validate if a number is a valid roster slot
 *
 * @param slot - Value to validate
 * @returns true if slot is valid (1-5), false otherwise
 *
 * @example
 * ```ts
 * isValidRosterSlot(1)  // true
 * isValidRosterSlot(6)  // false
 * isValidRosterSlot(0)  // false
 * ```
 */
export function isValidRosterSlot(slot: number): slot is RosterSlot {
    return Number.isInteger(slot) && slot >= 1 && slot <= 5;
}
